/**
 * A Machine Pool: the Environments an AFAR Project may place new threads on (ADR-0149). It only
 * constrains placement; each thread still runs on exactly one Environment for its whole life, and
 * a thread is never moved between them.
 */

/** One Environment in a project's pool, and the project there that threads start in. */
export interface MachinePoolEntry {
  readonly environmentId: string;
  readonly projectId: string;
  readonly label: string;
}

export type MachinePoolDecision<Reason extends string> =
  | { readonly outcome: "allowed" }
  | { readonly outcome: "refused"; readonly reason: Reason };

export function machinePoolAddition(
  pool: ReadonlyArray<MachinePoolEntry>,
  candidate: MachinePoolEntry,
  ownEnvironmentId: string,
): MachinePoolDecision<"own-environment" | "already-in-pool"> {
  if (candidate.environmentId === ownEnvironmentId)
    return { outcome: "refused", reason: "own-environment" };
  if (pool.some((existing) => existing.environmentId === candidate.environmentId))
    return { outcome: "refused", reason: "already-in-pool" };
  return { outcome: "allowed" };
}

/** Whether an agent may start a thread on an Environment: only one in its project's pool. */
export function machinePoolDelegation<Entry extends MachinePoolEntry>(
  pool: ReadonlyArray<Entry>,
  environmentId: string,
):
  | { readonly outcome: "allowed"; readonly entry: Entry }
  | { readonly outcome: "refused"; readonly reason: "not-in-pool" } {
  const entry = pool.find((candidate) => candidate.environmentId === environmentId);
  return entry === undefined
    ? { outcome: "refused", reason: "not-in-pool" }
    : { outcome: "allowed", entry };
}

/**
 * Load Balancing's candidates for a new thread in a project with a pool: the project's own
 * Environment and the pool's. A project without a pool is placed as before.
 */
export function restrictToMachinePool<Candidate extends { readonly environmentId: string }>(
  candidates: ReadonlyArray<Candidate>,
  pool: ReadonlyArray<MachinePoolEntry>,
  ownEnvironmentId: string,
): ReadonlyArray<Candidate> {
  if (pool.length === 0) return candidates;
  const allowed = new Set([ownEnvironmentId, ...pool.map((entry) => entry.environmentId)]);
  return candidates.filter((candidate) => allowed.has(candidate.environmentId));
}
